import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'

import i18n from '@app/i18n'


import { useAuth } from '@features/auth'
import { useErrorInfoModal, useModalStore } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods/model/useAddressStore'
import { useZonesStore } from '@features/shipping-methods/model/useZonesStore'
import { AddressItemType } from '@features/shipping-methods/types'

import { useSlots } from '@widgets/slots'

import { SHOP_ID } from '@shared/contants'
import {
  CustomerAddressInput,
  useSuggestionsLazyQuery,
  useZonesForGeoPointLazyQuery,
} from '@shared/generated'
import { useYandexSuggest } from '@shared/hooks/useYandexSuggest'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import LoaderCircle from '@shared/ui/loader-circle'
import Search from '@shared/ui/search'

import AddressList from './address/list'
import DeliveryAddressForm, { FormValues } from './form'
import MapDelivery from './MapDelivery'

import styles from './styles.module.scss'




type ActiveTab = 'map' | 'newAddress' | null;
interface GeoPoint {
  latitude: number;
  longitude: number;
}

type Props = {
  isDesktop: boolean;
  setHeaderConfig: React.Dispatch<
    React.SetStateAction<{
      title: string;
      icon?: boolean;
      onClick?: () => void;
      isTabs?: boolean;
      activeTab?: string;
      isMapRequired?: boolean;
    }>
  >;
};

export default function Delivery ({ isDesktop, setHeaderConfig }: Props) {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { t } = useTranslation('modalDelivery')
  const { t: ti } = useTranslation('info')
  const { t: to } = useTranslation('order')
  const { t: te } = useTranslation('errors')
  const { saveAddress } = useAddressStore()
  const [ _, setIsInvalidZoneBySuggest ] = useState(false)
  const [ activeTab, setActiveTab ] = useState<ActiveTab>(null)
  const [ geoPoint, setGeoPoint ] = useState<GeoPoint | null>(null)

  const { closeModal } = useModalStore()
  const { isAuthorized } = useAuth()
  const [ fetchSuggestionDetails ] = useSuggestionsLazyQuery()
  const {
    addAddress,
    setSelectedAddress,
    setDeliveryType,
    setShopId,
    setZoneId,
    selectedAddress,
  } = useAddressStore()
  const [ fetchZonesForGeopoint, { loading: loadingZonesForGeopoint } ] =
    useZonesForGeoPointLazyQuery()
  const {
    loadingAddreses,
    loadingCheckAddressGeopoint,
    zones,
    currentAddress,
    setCurrentAddress,
    setZones,
    fetchCheckAddressGeopoint,
  } = useZonesStore()
  const {
    query: searchInputAddress,
    setQuery: setSearchInputAddress,
    suggestions,
    clearSuggestions,
  } = useYandexSuggest({
    apiKey: import.meta.env.VITE_YANDEX_MAPS_API_KEY_SEARCH,
    lang: i18n.language === 'kk' ? 'kk_KZ' : 'ru_RU',
  })
  const CORRECT_ZONE_DELIVERY = useMemo(() => {
    if (zones === null) return null
    return zones && zones.length > 0
  }, [ zones ])
  const { openSlotsModal } = useSlots()
  useEffect(() => {
    if (activeTab === 'newAddress') {
      setHeaderConfig({
        title: t('new-address'),
        icon: true,
        onClick: () => {
          setActiveTab(null)
        },
        isTabs: false,
        isMapRequired: true,
      })
    }
    else {
      setHeaderConfig({ title: t('address'), isTabs: true, isMapRequired: false })
    }
    return () => {
      setHeaderConfig({ title: t('address'), isTabs: true, isMapRequired: false })
    }
  }, [ activeTab, setHeaderConfig, t ])

  useEffect(() => {
    if (currentAddress && searchInputAddress !== currentAddress) {
      setCurrentAddress(null)
      setZones(null)
      clearSuggestions()
    }
  }, [ searchInputAddress ])

  const handleButtonChange = useCallback(() => {
    setActiveTab('newAddress')
  }, [])

  const checkAddressForGeoPoint = async ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const coords = { latitude, longitude }
    setGeoPoint(coords)

    await fetchCheckAddressGeopoint(coords, () => {
      if (currentAddress) {
        setSearchInputAddress(currentAddress)
      }

      setActiveTab('newAddress')
    })
  }

  useEffect(() => {
    if (
      activeTab === 'newAddress' &&
      searchInputAddress &&
      suggestions.length === 0
    ) {
      setIsInvalidZoneBySuggest(true)
    }
  }, [ suggestions, searchInputAddress, activeTab ])

  useEffect(() => {
    if (searchInputAddress) {
      setIsInvalidZoneBySuggest(false)
    }
  }, [ searchInputAddress ])

  const handleSelectAddress = useCallback(
    async (address: AddressItemType) => {
      setSelectedAddress(address)

      if (address.customerGeoPoint) {
        try {
          const { data } = await fetchZonesForGeopoint({
            variables: {
              point: {
                latitude: address.customerGeoPoint.latitude,
                longitude: address.customerGeoPoint.longitude,
              },
            },
          })

          const zoneId =
            data?.zonesForGeoPoint?.[0]?.firstAvailableSlot?.zone_id
          if (zoneId) {
            setZoneId(zoneId)
            setDeliveryType('slot')
          }
        }
        catch (error) {
          console.error('Error fetching zones for address:', error)
        }
      }
    },
    [ fetchZonesForGeopoint, setDeliveryType, setSelectedAddress, setZoneId ],
  )

  useEffect(() => {
    if (selectedAddress) {
      handleSelectAddress(selectedAddress)
    }
  }, [ selectedAddress, handleSelectAddress ])

  const handleSaveAddress = async (formValues: FormValues) => {
    try {
      if (!geoPoint || !currentAddress) return

      const address: CustomerAddressInput = {
        address: currentAddress,
        address_appartments: formValues.apartment,
        address_floor: formValues.floor,
        address_porch: formValues.entrance,
        address_intercom: formValues.intercom,
        comment: formValues.comment,
        other_recipient: formValues.otherBuyer,
        recipient_name: formValues.otherBuyerName,
        recipient_phone: formValues.otherBuyerPhone,
        customerGeoPoint: geoPoint,
        label: formValues.title,
      }

      const addressWithId = {
        ...address,
        id: address.id || `guest-${nanoid()}`,
      }

      if (!isAuthorized) {
        addAddress(addressWithId as AddressItemType)
      }
      else {
        await saveAddress({
          addressInfo: address,
          geoPoint,
        })
      }

      await handleSelectAddress(addressWithId as AddressItemType)
      setActiveTab(null)

      const zones = await fetchZonesForGeopoint({
        variables: {
          point: {
            latitude: geoPoint.latitude,
            longitude: geoPoint.longitude,
          },
        },
      })

      const foundShopId = zones.data?.zonesForGeoPoint?.[0]?.shop_id
      if (foundShopId) {
        setShopId(foundShopId)
        closeModal('delivery')
        openSlotsModal()
      }
      else {
        setShopId(SHOP_ID)
      }
    }
    catch (error) {
      console.error('Error while saving address:', error)
      openErrorInfoModal({
        description: te('save_address'),
      })
    }
  }

  const handleSuggestionsClick = async (text: string) => {
    const res = await fetchSuggestionDetails({ variables: { address: text } })
    const suggestion = res.data?.getAddressSuggestions?.suggestions?.[0]
    const latitude = suggestion?.data?.geo_lat
    const longitude = suggestion?.data?.geo_lon
    if (latitude && longitude) {
      checkAddressForGeoPoint({ latitude: +latitude, longitude: +longitude })
    }
  }

  return (
    <div className={styles.root__container}>
      {(loadingZonesForGeopoint ||
        loadingAddreses ||
        loadingCheckAddressGeopoint) && (
        <div className={styles.root__loading}>
          <LoaderCircle className={styles['root__loading-circle']} />
        </div>
      )}

      <div className={cn(styles.root__content, activeTab === null && styles['root__content--empty'])}>
        {activeTab === 'newAddress' && (
          <>
            <Search
              value={currentAddress || searchInputAddress}
              handleChange={setSearchInputAddress}
              placeholder={t('input.placeholder-delivery')}
            />
            {!isDesktop && (
              <span
                onClick={() => setActiveTab('map')}
                className={styles.root__touchMap}
              >
                <ReactSVG src="/icons/map-pin.svg" wrapper="span" />
                {t('button.touch-map')}
              </span>
            )}
            {CORRECT_ZONE_DELIVERY === false && (
              <p className={styles.root__message}>
                {to('address.invalid_zone')}
              </p>
            )}
            {(CORRECT_ZONE_DELIVERY &&
              searchInputAddress &&
              !suggestions.length) ||
              (currentAddress && (
                <DeliveryAddressForm
                  onSubmit={handleSaveAddress}
                  isZone={CORRECT_ZONE_DELIVERY === true}
                />
              ))}

            {!currentAddress &&
              suggestions.map((el, i) => (
                <div
                  key={`${el.title}-${i}`}
                  onClick={() => {
                    handleSuggestionsClick(
                      el.title.text + ` ${el.subtitle?.text}`,
                    )
                  }}
                  className={styles.foundAddress}
                >
                  <span className={styles.foundAddress__title}>
                    {el.title.text}
                  </span>
                  {el.subtitle?.text && (
                    <span className={styles.foundAddress__subtitle}>
                      , {el.subtitle?.text}
                    </span>
                  )}
                </div>
              ))}
          </>
        )}
        {activeTab === null && (
          <>
            <p className={styles.content__desc}>{t('desc')}</p>
            <Button
              onClick={handleButtonChange}
              type="submit"
              theme="secondary"
              size={isDesktop ? 'large' : 'medium'}
            >
              {t('button.address')}
            </Button>
            <AddressList />
          </>
        )}
      </div>

      {!isDesktop && activeTab === 'map' && CORRECT_ZONE_DELIVERY === false && (
        <div className={styles.correct}>{to('address.invalid_zone')}</div>
      )}
      {!isDesktop && activeTab === 'map' && (
        <div onClick={() => setActiveTab(null)} className={styles.btn}>
          <ReactSVG
            src="/icons/list.svg"
            width={16}
            height={16}
            wrapper="span"
          />
          {ti('location.button')}
        </div>
      )}
      {(activeTab === 'newAddress') && (
        <MapDelivery
          newAddress={activeTab === 'newAddress'}
          onChangeAddressMap={checkAddressForGeoPoint}
        />
      )}
    </div>
  )
}
